/**
 * Variant Picker — shown after login when a user has more than one CUI
 * variant. Lets them choose the shared Standard-CUI or their own variant.
 * The own variant is started on-demand via /api/variant/ensure.
 */

import { useEffect, useRef, useState } from 'react';
import { useAuth, type CuiVariant } from '../contexts/AuthContext';
import { merkeVarianteWahl } from '../lib/varianteWahl';
import { VORLAUF_SEKUNDEN, nachEnsure, vorDemStart } from '../lib/variantePlan';

/** Target URL: the shared Standard-CUI lives on the bare domain, a user's own
 *  variant on the <port>. subdomain. */
function variantUrl(v: CuiVariant): string {
  const proto = window.location.protocol;
  if (v.shared) return `${proto}//partner.werking.tools`;
  return `${proto}//${v.port}.partner.werking.tools`;
}

/**
 * `autoPort`: die am Konto gemerkte ANDERE Variante (lib/varianteWahl.ts, Entscheid in
 * lib/variantePlan.ts). Der Picker oeffnet sie nach einem sichtbaren Vorlauf selbst; „Andere
 * Variante waehlen" steht von Anfang an da und haelt alles an. `hinweis`: Lese-/Speicherfehler.
 */
export default function VariantPicker({ onStay, autoPort = null, hinweis = '' }: { onStay: (fehler?: string) => void; autoPort?: number | null; hinweis?: string }) {
  const { user, currentPort } = useAuth();
  const [busyPort, setBusyPort] = useState<number | null>(null);
  const [status, setStatus] = useState('');
  const abgebrochen = useRef(false);
  const variants = user?.variants ?? [];
  const autoVariante = autoPort == null ? undefined : variants.find(x => x.port === autoPort);
  const [vorlauf, setVorlauf] = useState<number | null>(autoVariante ? VORLAUF_SEKUNDEN : null);
  const [autoAktiv, setAutoAktiv] = useState(!!autoVariante);

  /** Merkt die Wahl am Konto; ein Fehler wird angezeigt und kurz stehen gelassen, nie verschluckt. */
  async function merke(v: CuiVariant): Promise<string | undefined> {
    if (!user) return undefined;
    const r = await merkeVarianteWahl(user.id, v.port);
    if (r.ok) return undefined;
    setStatus(r.fehler);
    return r.fehler;
  }

  /** Haelt das automatische Oeffnen an — vor dem Start, waehrend ensure und vor der Weiterleitung. */
  function andereWaehlen() {
    abgebrochen.current = true;
    setAutoAktiv(false);
    setVorlauf(null);
    setBusyPort(null);
    setStatus('');
  }

  function weiterleiten(v: CuiVariant): boolean {
    if (abgebrochen.current) return false;
    window.location.href = variantUrl(v);
    return true;
  }

  async function choose(v: CuiVariant, automatisch = false) {
    // Eine Hand-Wahl hebt einen frueheren Abbruch auf; das automatische Oeffnen nie.
    if (!automatisch) { abgebrochen.current = false; setAutoAktiv(false); setVorlauf(null); }
    // Eine schon gemerkte Wahl wird beim Wiederoeffnen nicht erneut geschrieben.
    const fehler = automatisch ? undefined : await merke(v);
    const start = vorDemStart(v, currentPort, abgebrochen.current);
    if (start.art === 'abbruch') return;
    if (start.art === 'bleiben') { onStay(fehler); return; }
    // Vor dem Weiterleiten die Fehlermeldung lesbar lassen.
    if (fehler) await new Promise(r => setTimeout(r, 4000));
    if (start.art === 'weiterleiten') { weiterleiten(v); return; }

    // Own variant: ensure it is running (on-demand start), then redirect.
    setBusyPort(v.port);
    setStatus('Deine Variante wird vorbereitet…');
    try {
      for (let attempt = 0; attempt < 120; attempt++) {
        if (abgebrochen.current) return;
        const res = await fetch('/api/variant/ensure', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ port: v.port }),
        });
        const data = await res.json().catch(() => ({}));
        const schritt = nachEnsure({ ok: res.ok, status: data.status, error: data.error }, abgebrochen.current);
        if (schritt.art === 'abbruch') return;
        if (schritt.art === 'fehler') { setStatus(schritt.text); setBusyPort(null); return; }
        if (schritt.art === 'weiterleiten') { if (!weiterleiten(v)) setBusyPort(null); return; }
        setStatus('Variante startet… das kann eine Minute dauern.');
        await new Promise(r => setTimeout(r, 3000));
      }
      setStatus('Zeitüberschreitung — bitte später erneut versuchen.');
      setBusyPort(null);
    } catch {
      setStatus('Netzwerkfehler beim Start der Variante.');
      setBusyPort(null);
    }
  }

  // Gemerkte andere Variante: sichtbarer Vorlauf, dann einmal selbst oeffnen (nie in Schleife).
  useEffect(() => {
    if (vorlauf == null || !autoVariante) return;
    if (abgebrochen.current) return;
    if (vorlauf <= 0) { setVorlauf(null); void choose(autoVariante, true); return; }
    const t = setTimeout(() => setVorlauf(n => (n == null ? null : n - 1)), 1000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vorlauf]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--tnfh-0a0a0f)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      padding: '1rem',
    }}>
      <div style={{ maxWidth: 440, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#ffffff', marginBottom: 8 }}>
            Womit möchtest du arbeiten?
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>
            {user?.name ? `Angemeldet als ${user.name}` : 'Wähle deine CUI-Variante'}
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {variants.map(v => {
            const isBusy = busyPort === v.port;
            const anyBusy = busyPort !== null;
            return (
              <button
                key={v.port}
                data-ai-id={`variant-picker-choose-${v.port}`}
                onClick={() => !anyBusy && choose(v)}
                disabled={anyBusy}
                style={{
                  textAlign: 'left',
                  padding: '18px 20px',
                  background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${isBusy ? 'color-mix(in srgb, var(--tnf-dec15e) 50%, transparent)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: 14,
                  color: '#ffffff',
                  cursor: anyBusy ? 'default' : 'pointer',
                  opacity: anyBusy && !isBusy ? 0.4 : 1,
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontSize: 16, fontWeight: 600 }}>{v.label}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                  {v.shared ? 'Gemeinsame Standard-Variante' : 'Deine eigene Variante'}
                  {v.port === currentPort ? ' · aktuell aktiv' : ''}
                </div>
              </button>
            );
          })}
        </div>

        {autoAktiv && autoVariante && (
          <p data-variante-vorlauf style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', textAlign: 'center', marginTop: 20 }}>
            {vorlauf != null && vorlauf > 0
              ? `Öffne deine gemerkte Variante „${autoVariante.label}“ in ${vorlauf} s …`
              : `Öffne deine gemerkte Variante „${autoVariante.label}“ …`}
          </p>
        )}

        {(autoAktiv || busyPort !== null) && (
          <p style={{ textAlign: 'center', marginTop: 12 }}>
            <button
              data-ai-id="variant-picker-andere"
              onClick={andereWaehlen}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}
            >
              Andere Variante wählen
            </button>
          </p>
        )}

        {hinweis && (
          <p data-variante-hinweis style={{ fontSize: 13, color: 'var(--tn-orange, #e0a040)', textAlign: 'center', marginTop: 20 }}>
            {hinweis}
          </p>
        )}

        {status && (
          <p style={{ fontSize: 13, color: 'color-mix(in srgb, var(--tnf-dec15e) 80%, transparent)', textAlign: 'center', marginTop: 20 }}>
            {status}
          </p>
        )}
      </div>
    </div>
  );
}
